import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ChatIcon from "@mui/icons-material/Chat";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { KNOWLEDGEX_OCR_ASK_URL } from "../config/api";

const DocumentChat = ({ documentText, disabled }) => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const openChat = () => {
        if (!documentText?.trim()) return;
        setOpen(true);
        if (messages.length === 0) {
            setMessages([
                {
                    role: "assistant",
                    content: "I've read your document. Ask me anything about its content.",
                },
            ]);
        }
    };

    const sendMessage = async () => {
        const query = input.trim();
        if (!query || loading || !documentText?.trim()) return;

        setMessages((prev) => [...prev, { role: "user", content: query }]);
        setInput("");
        setLoading(true);

        try {
            const { data } = await axios.post(
                KNOWLEDGEX_OCR_ASK_URL,
                {
                    ocr_text: documentText,
                    query,
                    session_id: sessionId,
                },
                { withCredentials: true }
            );
            if (data.session_id) setSessionId(data.session_id);
            setMessages((prev) => [...prev, { role: "assistant", content: data.answer || "No answer." }]);
        } catch (err) {
            const detail = err.response?.data?.detail || "Failed to get an answer.";
            setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${detail}` }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                variant="contained"
                onClick={openChat}
                disabled={disabled || !documentText?.trim()}
                startIcon={<ChatIcon />}
                sx={{
                    background: "linear-gradient(90deg, rgb(112, 68, 255) 0%, rgb(238, 60, 255) 100%)",
                    color: "white",
                    padding: "10px 30px",
                    borderRadius: "25px",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: "bold",
                    "&:disabled": { background: "#94A3B8" },
                }}
            >
                Chat with Document
            </Button>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { backgroundColor: "#282c34", color: "#fff", minHeight: "70vh" } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Document Q&A
                    </Typography>
                    <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Paper sx={{ height: 400, overflowY: "auto", padding: 2, backgroundColor: "#1e293b", marginBottom: 2 }}>
                        {messages.map((msg, idx) => (
                            <Box key={idx} sx={{ marginBottom: 2, textAlign: msg.role === "user" ? "right" : "left" }}>
                                <Paper
                                    sx={{
                                        display: "inline-block",
                                        padding: 1.5,
                                        maxWidth: "90%",
                                        backgroundColor: msg.role === "user" ? "#7044ff" : "#334155",
                                        color: "#fff",
                                    }}
                                >
                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                </Paper>
                            </Box>
                        ))}
                        {loading && <CircularProgress size={24} sx={{ color: "#EE3CFF" }} />}
                        <div ref={bottomRef} />
                    </Paper>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField
                            fullWidth
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            placeholder="Ask a question about this document..."
                            sx={{ "& .MuiInputBase-input": { color: "#e2e8f0" } }}
                        />
                        <Button
                            variant="contained"
                            onClick={sendMessage}
                            disabled={loading || !input.trim()}
                            sx={{ backgroundColor: "#EE3CFF", minWidth: 56 }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DocumentChat;
